import React, { useMemo, useState } from 'react';
import { Badge, Table as BTable, TableProps as BTableProps, Card } from 'react-bootstrap';
import {
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';

import { TableProps, TableState } from './types';
import { TableComponentsCommonPropsContext, TableStateContext } from './context';
import { Header } from './Header';
import Foot from './Foot';
import { Body } from './Body';
import FilterForm from './Filters';

export const Table: React.FC<TableProps> = ({
    data,
    isLoading,
    isGroupedTable,
    filterSchema,
    filterInitialState,
    headerText,
    showCountInHeader,
    headerButtons,
    theme,
    isAdministrator,
    totalCount,
    totalPages,
    // tableRef,
    ...rest
}) => {
    const filterFns = useMemo(
        () => ({
            multiSelect: (row, columnId, filterValues) => {
                if (!filterValues || filterValues.length === 0) return true; // No filter applied
                return filterValues.includes(row.getValue(columnId));
            },
            numberFilter: (row, columnId, filterValues) => {
                if (!filterValues || filterValues.length === 0) return true; // No filter applied
                return filterValues === row.getValue(columnId);
            },
            dateBetween: (row, columnId, filterValues) => {
                if (filterValues.length === 0) return row;

                const start = filterValues[0] ? new Date(filterValues[0]) : null;
                const end = filterValues[1] ? new Date(filterValues[1]) : null;

                // Set the end date to the end of the day if no specific time is provided
                if (end && end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0) {
                    end.setHours(23, 59, 59, 999);
                }

                const recordDate = new Date(row.getValue(columnId));

                if (start && end) {
                    // Check if the recordDate is between the start and end dates
                    return recordDate >= start && recordDate <= end;
                } else if (start) {
                    // Check if the recordDate is on or after the start date
                    return recordDate >= start;
                } else if (end) {
                    // Check if the recordDate is on or before the end date
                    return recordDate <= end;
                }

                // If neither start nor end is provided, return the row (this case might be redundant based on the initial check)
                return row;
            },
        }),
        [],
    );
    const [columns] = React.useState<typeof rest.columns>(() => [...rest.columns]);
    const hiddenColumns = columns
        .filter((column) => column.show === false)
        .reduce((acc, column) => {
            acc[column.accessorKey ?? column.id ?? ''] = false;
            return acc;
        }, {});

    const table = useReactTable({
            initialState: rest.initialState,
            columns,
            state: {
                columnVisibility: hiddenColumns,
            },
            data,
            filterFns,
            getCoreRowModel: getCoreRowModel(),
            getPaginationRowModel: getPaginationRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            getExpandedRowModel: getExpandedRowModel(),
            getSortedRowModel: getSortedRowModel(),
            globalFilterFn: (row, columnId, filterValue) => {
                const safeValue = (() => {
                    const value = row.getValue(columnId) as string;
                    return typeof value === 'number' ? String(value) : value;
                })();
                return safeValue?.toLowerCase().includes(filterValue.toLowerCase());
            },
        }),
        [tableState, setTableState] = useState<TableState>({
            activePage: 1,
            pageSize: rest.initialState?.pagination?.pageSize || (isAdministrator ? 50 : 10),
        });
    const filter =
        filterSchema !== null && filterSchema !== undefined ? (
            <div className={'search'}>
                <FilterForm rows={table.getRowModel().rows} table={table} filterSchema={filterSchema} initialState={filterInitialState} />
            </div>
        ) : (
            <></>
        );
    const header =
        headerText !== null && headerText !== undefined ? (
            theme === 'card' ? (
                <Card.Header>{headerText}</Card.Header>
            ) : (
                <div className="module-header border-bottom d-flex justify-content-between flex-column flex-md-row bg-white rounded-top p-3">
                    <h2 className="m-0 p-0 col position-relative">
                        {headerText}
                        {showCountInHeader === true ? (
                            <Badge bg="warning" className="position-absolute border-primary badge-middle text-primary" pill>
                                {(data && data.length) || 0}
                            </Badge>
                        ) : (
                            <></>
                        )}
                    </h2>
                    {headerButtons !== null && headerButtons !== undefined ? headerButtons : <></>}
                </div>
            )
        ) : (
            <></>
        );
    return (
        <>
            {header}
            {filter}
            <TableStateContext.Provider value={[tableState, setTableState]}>
                <TableComponentsCommonPropsContext.Provider
                    value={{
                        ...rest,
                        columns,
                        data,
                        isLoading,
                        isGroupedTable,
                        table,
                        isAdministrator,
                        // Use provided totalCount if available (for accurate pagination), otherwise fall back to data length
                        totalItems: totalCount !== undefined ? totalCount : data ? data.length : 0,
                        // Use provided totalPages if specified, allows override of the automatic calculation
                        totalPageOverride: totalPages,
                    }}
                >
                    {isLoading ? (
                        <span>Loading....</span>
                    ) : (
                        <>
                            <div className="grid-wrapper">
                                <BTable {...(rest as BTableProps)}>
                                    <Header headerGroups={table.getHeaderGroups()} headerClassName={rest.headerRowClass} />
                                    <Body rowModel={table.getRowModel()} emptyText={rest.emptyText} />
                                </BTable>
                            </div>
                            <Foot />
                        </>
                    )}
                </TableComponentsCommonPropsContext.Provider>
            </TableStateContext.Provider>
        </>
    );
};

export default Table;
